import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DraggableImage } from '@/components/interAction/Drag/DraggableImage'
import { TargetWord } from '@/components/interAction/Drag/TargetWord'
import { AnimatedLine } from '@/components/interAction/Line'
import * as dragStyles from '@/components/interAction/Drag/DragStyle.css'
import * as styles from '@/components/organisms/Learning/LearningSytle.css'
import { vars } from '@/style/theme.css'
import GradingState from '@/components/organisms/Grading/GradingState'

interface GradingDragMatchProps {
  activity?: any
  responseJson?: string
}

const GradingDragMatch = ({ activity, responseJson }: GradingDragMatchProps) => {
  // 1) activity.items[0].contentJson 에서 flashCards 파싱 (학습 화면과 동일 구조)
  const flashCards = useMemo(() => {
    try {
      const item = activity?.items?.[0]
      if (!item) return [] as any[]
      const raw = item.contentJson || '{}'
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
      const cards = parsed.flashCards || []
      return Array.isArray(cards) ? cards : []
    } catch (e) {
      console.error('GradingDragMatch: contentJson 파싱 에러', e)
      return [] as any[]
    }
  }, [activity])

  // 2) responseJson 파싱 (학습 화면 existingResponse와 동일 키 사용)
  const existingResponse = useMemo(() => {
    if (!responseJson) return {}
    try {
      return JSON.parse(responseJson)
    } catch (e) {
      console.error('GradingDragMatch: responseJson 파싱 에러', e)
      return {}
    }
  }, [responseJson])

  // 3) flashCards → 이미지/단어 리스트 생성 (학습 화면 로직 축약)
  const baseImageList = useMemo(() => {
    return flashCards.map((card: any, index: number) => {
      const content = typeof card.contentJson === 'string' ? JSON.parse(card.contentJson) : card.contentJson
      return {
        id: `item-${index}`,
        img: content?.wordImageUrl || content?.imageUrl || '',
        word: content?.word || '',
        meaning: content?.meaning || ''
      }
    })
  }, [flashCards])

  const baseWordList = useMemo(() => {
    return flashCards.map((card: any, index: number) => {
      const content = typeof card.contentJson === 'string' ? JSON.parse(card.contentJson) : card.contentJson
      return {
        id: `item-${index}`,
        label: content?.word || '',
        meaning: content?.meaning || ''
      }
    })
  }, [flashCards])

  // 4) 응답의 섞인 순서가 있으면 반영
  const imageList = useMemo(() => {
    const order = existingResponse?.shuffledImageOrder
    if (Array.isArray(order) && order.length > 0) {
      return order.map((id: string) => baseImageList.find((it) => it.id === id)!).filter(Boolean)
    }
    return baseImageList
  }, [existingResponse, baseImageList])

  const wordList = useMemo(() => {
    const order = existingResponse?.shuffledWordOrder
    if (Array.isArray(order) && order.length > 0) {
      return order.map((id: string) => baseWordList.find((it) => it.id === id)!).filter(Boolean)
    }
    return baseWordList
  }, [existingResponse, baseWordList])

  // 이미지가 하나도 없으면 텍스트(해석)만 표시 (ESSAY/TRANSLATION 호환)
  const isTextOnly = useMemo(() => {
    return imageList.every((it: any) => !it.img)
  }, [imageList])

  // per-item 및 overall 채점 상태 계산
  const perItemStatus = useMemo(() => {
    const matched = existingResponse?.matched || {}
    const statusMap: Record<string, 'correct' | 'incorrect' | 'grading'> = {}
    wordList.forEach(({ id }: any) => {
      const v = matched ? matched[id] : undefined
      if (v === undefined || v === null) statusMap[id] = 'grading'
      else statusMap[id] = v === id ? 'correct' : 'incorrect'
    })
    return statusMap
  }, [existingResponse, wordList])

  // 5) 매칭/라인 복원
  const containerRef = useRef<HTMLDivElement | null>(null)
  const fromRefs = useRef<Record<string, HTMLSpanElement | null>>({})
  const toRefs = useRef<Record<string, HTMLSpanElement | null>>({})
  const [lines, setLines] = useState<Array<{ id: string; x1: number; y1: number; x2: number; y2: number; status: 'correct' | 'incorrect' }>>([])

  useEffect(() => {
    const matched = existingResponse?.matched || {}
    // 항상 현재 DOM 위치 기반으로 라인 재계산 (homework/doing과 동일한 방식)
    const timer = setTimeout(() => {
      const container = containerRef.current
      if (!container) { setLines([]); return }
      const containerRect = container.getBoundingClientRect()
      if (matched && Object.keys(matched).length > 0) {
        const newLines: typeof lines = []
        Object.keys(matched).forEach((toId: string) => {
          const matchValue = matched[toId]
          if (!matchValue) return
          const fromId = typeof matchValue === 'string' ? matchValue : toId
          const fromEl = fromRefs.current[fromId]
          const toEl = toRefs.current[toId]
          if (fromEl && toEl) {
            const fromRect = fromEl.getBoundingClientRect()
            const toRect = toEl.getBoundingClientRect()
            const baseLine = {
              id: `${fromId}-${toId}`,
              x1: fromRect.left - containerRect.left + fromRect.width / 2,
              y1: fromRect.top - containerRect.top + fromRect.height / 2,
              x2: toRect.left - containerRect.left + toRect.width / 2,
              y2: toRect.top - containerRect.top + toRect.height / 2,
              status: fromId === toId ? 'correct' : 'incorrect' as 'correct' | 'incorrect'
            }
            newLines.push(baseLine)

            if (fromId !== toId) {
              const correctFromEl = fromRefs.current[toId]
              if (correctFromEl) {
                const correctRect = correctFromEl.getBoundingClientRect()
                newLines.push({
                  id: `${fromId}-${toId}-correct`,
                  x1: correctRect.left - containerRect.left + correctRect.width / 2,
                  y1: correctRect.top - containerRect.top + correctRect.height / 2,
                  x2: toRect.left - containerRect.left + toRect.width / 2,
                  y2: toRect.top - containerRect.top + toRect.height / 2,
                  status: 'correct'
                })
              }
            }
          }
        })
        setLines(newLines)
      } else {
        setLines([])
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [existingResponse, imageList, wordList])

  // 6) 데이터 없는 경우
  if (flashCards.length === 0) {
    return (
      <div className='p-5'>
        <p className='text-center text-gray-500'>드래그 매치 데이터가 없습니다.</p>
      </div>
    )
  }

  return (
    <div className=''>
      <p className='mb-3 text-lg font-bold'>드래그 앤 드롭</p>
      <div ref={containerRef} className='flex relative gap-2 justify-between drag-match-container'>
        <div className='flex flex-col gap-2'>
          {imageList.map(({ id, img, meaning }: any) => (
            <div key={id} className='flex gap-2 items-center'>
              {isTextOnly ? (
                <div className={`${dragStyles.imageWrapper} bg-white px-2`}>
                  <p className='text-sm font-medium text-gray-700 text-center line-clamp-3 leading-tight'>{meaning}</p>
                </div>
              ) : (
                <DraggableImage id={id} img={img} disabled={true} />
              )}
              <span className={styles.dot({ status: (existingResponse?.matched && Object.values(existingResponse.matched).includes(id)) ? 'correct' : 'default' })} ref={(el) => { fromRefs.current[id] = el }}></span>
            </div>
          ))}
        </div>
        <div className='flex flex-col gap-2'>
          {wordList.map(({ id, label }: any) => (
            <div key={id} className='flex gap-2 items-center'>
              <span className={styles.dot({ status: (existingResponse?.matched && existingResponse?.matched[id]) ? 'correct' : 'default' })} ref={(el) => { toRefs.current[id] = el }}></span>
              <TargetWord id={id} label={label} isMatched={Boolean(existingResponse?.matched && existingResponse?.matched[id])} />
              <div className='ml-2'>
                <GradingState status={perItemStatus[id]} />
              </div>
            </div>
          ))}
        </div>
        <svg className="absolute inset-0 z-10 w-full h-full pointer-events-none">
          {lines.map((line) => (
            <AnimatedLine
              key={line.id}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              color={line.status === 'correct' ? vars.color.green[500] : vars.color.gray[400]}
            />
          ))}
        </svg>
      </div>
    </div>
  )
}

export default GradingDragMatch